'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { CustomDropdown, type DropdownItem } from '@/app/components/CustomDropdown'
import { FAQGroup } from '@/app/components/FAQGroup'
import { FAQNav } from '@/app/components/FAQNav'
import { slugify } from '@/app/utils/text'
import type { FaqQueryResult } from '@/sanity.types'

type FAQAreaClientProps = {
  faqData: FaqQueryResult | null
}

/**
 * Client component that provides interactive FAQ filtering.
 * This is rendered by the server component FAQArea after data fetching.
 */
export function FAQAreaClient({ faqData }: FAQAreaClientProps) {
  const categories = useMemo(() => faqData?.categories ?? [], [faqData?.categories])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [displayedCategories, setDisplayedCategories] = useState(categories)
  const [shouldScroll, setShouldScroll] = useState(false)
  const faqContentRef = useRef<HTMLDivElement>(null)

  const menuCategories = categories.filter((category) => !category.hideFromMenu)

  const dropdownItems: DropdownItem[] = [
    { value: 'all', label: 'All Questions' },
    ...menuCategories.map((category) => ({
      value: slugify(category.title ?? 'untitled'),
      label: category.title ?? 'Untitled',
    })),
  ]

  useEffect(() => {
    const filtered =
      selectedCategory === 'all'
        ? categories
        : categories.filter(
            (category) => slugify(category.title ?? 'untitled') === selectedCategory,
          )

    setDisplayedCategories(filtered)
  }, [selectedCategory, categories])

  useEffect(() => {
    if (shouldScroll && faqContentRef.current) {
      const timeoutId = setTimeout(() => {
        if (faqContentRef.current) {
          faqContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        setShouldScroll(false)
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [displayedCategories, shouldScroll])

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value)
    setShouldScroll(true)
  }

  return (
    <div className="content-container mt-2xl mb-7xl lg:mt-5xl lg:mb-11xl">
      <div className="w-full max-w-[434px] mx-auto lg:max-w-none lg:flex lg:gap-5xl">
        {menuCategories.length > 0 && (
          <div className="mb-2xl lg:hidden">
            <CustomDropdown
              items={dropdownItems}
              value={selectedCategory}
              placeholder="Select a category"
              onChange={handleCategoryChange}
            />
          </div>
        )}

        <div className="hidden rounded-xl bg-neutral-50 p-5xl w-[310px] flex-shrink-0 flex-grow-0 self-start sticky top-[calc(66px+theme(spacing.5xl))] lg:block">
          <FAQNav
            categories={menuCategories}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
          />
        </div>

        <div ref={faqContentRef} className="flex flex-col gap-2xl flex-1 lg:min-h-[600px]">
          {displayedCategories.length > 0 && (
            <FAQGroup
              categories={[displayedCategories[0]]}
              className="[box-shadow:inset_0_0_0_4px_theme(colors.primary.100)]"
            />
          )}
          {displayedCategories.length > 1 && <FAQGroup categories={displayedCategories.slice(1)} />}
        </div>
      </div>
    </div>
  )
}
